/**
 * Выбор программы списком, а не полем с автодополнением.
 *
 * Программ 49, и все известны заранее — печатать их название незачем, а вреда
 * от печати достаточно: названия вроде «А что случилось?» набираются с
 * опечаткой, а опечатка в фильтре даёт нерешённое имя вместо выборки.
 *
 * Человек остался автодополнением: людей 279, списком это не выбирают.
 */

import { useEffect, useState } from "react";

import { suggestPrograms } from "../api/client";

export function ProgramPicker({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (value: string | null) => void;
}) {
  const [titles, setTitles] = useState<string[]>([]);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    suggestPrograms("")
      .then((data) => {
        if (!live) return;
        // Сортировка на клиенте, а не в запросе: русский алфавит зависит от
        // collation базы, и она разная у Postgres и у SQLite, на котором идут
        // тесты. localeCompare даёт один порядок везде и правильно ставит «Ё».
        // Скрытые в справочнике из списка выбрасываются, но приходят с
        // сервера с пометкой — так выбранная скрытая программа не подпишется
        // «нет в справочнике», то есть экран не соврёт про своё состояние.
        setTitles(
          data.results
            .filter((row) => !row.is_hidden)
            .map((row) => row.title)
            .sort((left, right) => left.localeCompare(right, "ru")),
        );
        setHidden(new Set(data.results.filter((row) => row.is_hidden).map((row) => row.title)));
      })
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, []);

  // Значение, которого нет в списке, обязано остаться видимым. В это поле
  // пишет не только человек: реплика в чате ставит программу по разбору
  // модели, и она может не совпасть с названием из справочника. Молча выбрать
  // вместо него первую строку списка значило бы показывать один фильтр, а
  // считать по другому.
  const selected = value !== null && value !== "";
  const isHidden = selected && hidden.has(value);
  const unknown = selected && !titles.includes(value) && !isHidden;

  return (
    <div className="field">
      <label className="field__label">Программа</label>
      <div className="field__control">
        <select
          className="input"
          value={value ?? ""}
          onChange={(event) => onChange(event.target.value || null)}
        >
          <option value="">не выбрана</option>
          {isHidden && <option value={value}>{value} — скрыта в справочнике</option>}
          {unknown && <option value={value}>{value} — нет в справочнике</option>}
          {titles.map((title) => (
            <option key={title} value={title}>
              {title}
            </option>
          ))}
        </select>
      </div>
      {/* Пустой список выглядел бы как «программ нет». Причина названа. */}
      {failed && <p className="error">Справочник программ не загрузился.</p>}
    </div>
  );
}
